import { useEffect, useMemo, useRef } from "react";
import { useIsomorphicLayoutEffect } from "./layout-effect";
import { useReRender } from "./re-render";

type Self = {
  isActive: boolean;

  active: {
    promise: Promise<void>;
    resolve: () => void;
  };

  inactive: {
    promise: Promise<void>;
    resolve: () => void;
  };

  showIndicatorTimeout?: number;
  hideIndicatorTimeout?: number;

  /**
   * timestamp for when the loader was displayed; used in order to determine
   * when it can be safely hidden again.
   */
  showedIndicatorAt?: number;
};

/**
 * conditionally enable an activity indicator only after we've been waiting for
 * the external value to be true for the specified delay; once activity
 * indicator has been displayed retain it for at least the defined duration.
 */
export function useActivityIndicator(
  isActive: boolean,
  {
    delayMs = 125,
    durationMs = 500,
    allowInitial,
  }: {
    delayMs?: number;
    durationMs?: number;

    /**
     * if enabled, then the activityIndicator will be displayed immediately
     * upon the first display if isActive is initally set to true
     */
    allowInitial?: boolean;
  } = {}
): {
  isActive: () => boolean;
  getActivePromise: () => Promise<void>;
  getInactivePromise: () => Promise<void>;
} {
  const reRender = useReRender();

  const { current: self } = useRef<Self>({
    isActive: allowInitial ? isActive : false,
    active: createExternallyResolvablePromise(),
    inactive: createExternallyResolvablePromise(),
  });

  // In case the activity indicator should be active on mount, then ensure our
  // promise reflects this
  useIsomorphicLayoutEffect(() => {
    if (isActive && allowInitial) {
      self.active.resolve();
      self.showedIndicatorAt = Date.now();
    } else {
      self.inactive.resolve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isActive) {
      window.clearTimeout(self.hideIndicatorTimeout);

      if (!self.isActive) {
        window.clearTimeout(self.showIndicatorTimeout);

        self.showIndicatorTimeout = window.setTimeout(() => {
          self.isActive = true;
          self.active.resolve();
          self.inactive = createExternallyResolvablePromise();
          reRender();

          self.showedIndicatorAt = Date.now();
        }, delayMs);
      }
    }

    if (!isActive) {
      window.clearTimeout(self.showIndicatorTimeout);

      if (self.isActive) {
        window.clearTimeout(self.hideIndicatorTimeout);

        // determine how much time will have passed before the loader is going
        // to be hidden based solely on the durationMs and currently elapsed
        // time
        const elapsedDuration = self.showedIndicatorAt
          ? Date.now() - self.showedIndicatorAt
          : 0;

        const hideLoaderAfter = Math.max(elapsedDuration, durationMs);

        // now hide the loader once the timeout has been completed
        self.hideIndicatorTimeout = window.setTimeout(() => {
          self.isActive = false;
          self.inactive.resolve();
          self.active = createExternallyResolvablePromise();
          reRender();
        }, hideLoaderAfter - elapsedDuration);
      }
    }
  }, [isActive, delayMs, durationMs, self, reRender]);

  return useMemo(
    () => ({
      isActive: () => self.isActive,
      getActivePromise: () => self.active.promise,
      getInactivePromise: () => self.inactive.promise,
    }),
    [self.active.promise, self.inactive.promise, self.isActive]
  );
}

function createExternallyResolvablePromise(): {
  promise: Promise<void>;
  resolve: () => void;
} {
  let resolve: () => void = () => {
    /** no-op */
  };
  const promise = new Promise<void>((_resolve) => {
    resolve = _resolve;
  });

  return { promise, resolve };
}
